import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { catchError, firstValueFrom, of, tap } from 'rxjs';

import { ValidationService } from '../../../utils/valibot/validation.service';
import {
  GetConsentChoicesQuery,
  PolicyDocumentQuery,
  PreregistrationRequest,
  SetConsentChoicesRequest,
} from './model/request.model';
import {
  GcdmConsents,
  GcdmConsentsSchema,
  PolicyDocumentSchema,
  PreregistrationResponse,
  PreregistrationResponseSchema,
} from './model/response.model';

@Injectable({
  providedIn: 'root',
})
export class GcdmClient {
  private static readonly urlPrefix = `gcdm`;

  private readonly http = inject(HttpClient);
  private readonly logger = inject(NGXLogger);
  private readonly validationService = inject(ValidationService);

  getPolicyDocument(options: PolicyDocumentQuery) {
    let queryParams = new HttpParams();
    const { policyDocumentName, ...query } = options;

    Object.entries(query).forEach(([key, value]) => {
      if (value != undefined) {
        queryParams = queryParams.set(key, value as string);
      }
    });

    const url = `${GcdmClient.urlPrefix}/documents/${policyDocumentName}`;
    this.logger.info(`#CDFTF0032 Loading policy document from ${url}`);
    return firstValueFrom(
      this.http
        .get(url, {
          params: queryParams,
        })
        .pipe(
          this.validationService.pipeResponse(PolicyDocumentSchema),
          tap({
            error: (error) => this.logger.error('#CDFTF0033 Unable to load policy document', error),
          }),
        ),
    );
  }

  getNscConsentChoices(options: GetConsentChoicesQuery): Promise<GcdmConsents> {
    let queryParams = new HttpParams();
    const { gcid, ...query } = options;

    Object.entries(query).forEach(([key, value]) => {
      if (value != undefined) {
        queryParams = queryParams.set(key, value as string);
      }
    });

    const url = `${GcdmClient.urlPrefix}/consents/${gcid}`;
    this.logger.info(`#CDFTF0032 Loading consents from ${url}`);
    return firstValueFrom(
      this.http
        .get(url, {
          params: queryParams,
        })
        .pipe(
          this.validationService.pipeResponse(GcdmConsentsSchema),
          catchError((error) => {
            if (error && error instanceof HttpErrorResponse && error.status === 404) {
              this.logger.info('#CDFFF0197 No consents found for the given GCID');
              return of([]);
            } else {
              this.logger.error('#CDFTF0033 Unable to load consents', error);
              throw error;
            }
          }),
        ),
    );
  }

  setConsentChoices(gcid: string, consents: SetConsentChoicesRequest[]) {
    const url = `${GcdmClient.urlPrefix}/consents/${gcid}`;
    this.logger.info(`#CDFTF0041 Saving consent choices using ${url}`);
    return firstValueFrom(
      this.http.post<void>(url, consents).pipe(
        tap({
          error: (error) => this.logger.error('#CDFTF0040 Unable to set consent choices', error),
        }),
      ),
    );
  }

  preregister(request: PreregistrationRequest): Promise<PreregistrationResponse> {
    const url = `${GcdmClient.urlPrefix}/preregistration`;
    this.logger.info(`#CDFTF0051 Performing preregistration using ${url}`);
    return firstValueFrom(
      this.http.post(url, request).pipe(
        this.validationService.pipeResponse(PreregistrationResponseSchema),
        tap({
          next: (response: PreregistrationResponse) => {
            if (!response.successes?.length) {
              const error = '#CDFTF0053 Unable to perform preregistration, response did not contain success';
              this.logger.error(error, response);
              throw new Error(error);
            }
          },
          error: (error) => this.logger.error('#CDFTF0052 Unable to perform preregistration', error),
        }),
      ),
    );
  }
}
